import {useCallback, useContext, useEffect, useState} from 'react';
import {doc, getDoc, setDoc} from 'firebase/firestore';

import {db} from '-/firebase';
import {UserContext} from '-/contexts/User';
import {reportError} from '-/data/errorReporting';

// Recent searches + recently-opened records, persisted to userPreferences/{uid} —
// the same doc WhatsNewDialog (whatsnewSeenVersion) and useHelpState use — so the
// dropdown's idle state follows a member between the hall computer and their
// phone rather than being stranded in one browser's localStorage.

const MAX_RECENT_SEARCHES = 6;
const MAX_RECENT_RECORDS = 5;

const SEARCHES_FIELD = 'recentSearches';
const RECORDS_FIELD = 'recentSearchRecords';

export default function useRecentSearches() {
    const {currentUser} = useContext(UserContext);
    const uid = currentUser?.uid;

    const [searches, setSearches] = useState([]);
    const [records, setRecords] = useState([]);

    useEffect(() => {
        if (!uid) return undefined;

        let cancelled = false;
        (async () => {
            try {
                const snap = await getDoc(doc(db, 'userPreferences', uid));
                if (cancelled) return;
                const data = snap.exists() ? snap.data() : {};
                setSearches(Array.isArray(data[SEARCHES_FIELD]) ? data[SEARCHES_FIELD] : []);
                setRecords(Array.isArray(data[RECORDS_FIELD]) ? data[RECORDS_FIELD] : []);
            } catch (e) {
                // A member with no prefs doc yet is the normal case, and search
                // works perfectly well without history — never surface this.
                //
                // A permission-denied is the auth-transition race the rest of the
                // app already swallows on this same doc (WhatsNewDialog.load,
                // snapshotErrorHandler): the shell is still mounted — so this
                // hook is still reading — while the session is torn down, just
                // before the redirect to /login (SICAMOUSFD-13). The
                // userPreferences rule only requires an authenticated session for
                // your own uid, so it can never be a real authorization failure.
                if (e?.code !== 'permission-denied') {
                    reportError(e, {context: 'useRecentSearches.load', component: 'GlobalSearch'});
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [uid]);

    // The whole array is rewritten rather than merged: it's an ordered,
    // deduped, capped list, which is not something a field merge can express.
    const persist = useCallback((field, value) => {
        if (!uid) return;
        setDoc(doc(db, 'userPreferences', uid), {[field]: value}, {merge: true})
            .catch(e => {
                // Same auth-transition race as load — see above.
                if (e?.code !== 'permission-denied') {
                    reportError(e, {context: `useRecentSearches.${field}`, component: 'GlobalSearch'});
                }
            });
    }, [uid]);

    const rememberSearch = useCallback(query => {
        const text = String(query || '').trim();
        if (!text) return;
        setSearches(previous => {
            const next = [text, ...previous.filter(entry => entry.toLowerCase() !== text.toLowerCase())]
                .slice(0, MAX_RECENT_SEARCHES);
            persist(SEARCHES_FIELD, next);
            return next;
        });
    }, [persist]);

    const rememberRecord = useCallback(result => {
        if (!result?.url) return;
        // Only what the row needs to re-render — never the snippet, which is
        // relative to a query that is over.
        const entry = {
            type: result.type,
            id: result.id,
            url: result.url,
            title: result.title,
            subtitle: result.subtitle || '',
            at: Date.now()
        };
        setRecords(previous => {
            const next = [entry, ...previous.filter(item => item.url !== entry.url)].slice(0, MAX_RECENT_RECORDS);
            persist(RECORDS_FIELD, next);
            return next;
        });
    }, [persist]);

    const clear = useCallback(() => {
        setSearches([]);
        persist(SEARCHES_FIELD, []);
    }, [persist]);

    return {searches, records, rememberSearch, rememberRecord, clear};
}
